import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  CssBaseline,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import UsageIcon from "@mui/icons-material/DataUsage";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ImpactIcon from '@mui/icons-material/Assessment';
import { ReactComponent as Logo } from "../assets/guardify-logo.svg"; // Import your SVG logo
import { useAppContext } from '../store/AppContext';
import { AccountCircle } from '@mui/icons-material';

const drawerWidth = 240;
const collapsedWidth = 70;

const menuItems = [
  { text: "Usage", icon: <UsageIcon />, path: "/" },
  { text: "Impact", icon: <ImpactIcon />, path: "/impact" },
];

const Layout = ({ signOut, user, children, hideNav, productName }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const { state, dispatch } = useAppContext();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCohortSwitch = (cohortId) => {
    dispatch({ type: 'SWITCH_COHORT', payload: cohortId });
    handleClose();
  };

  const handleSignOut = () => {
    handleClose();
    signOut();
  };

  const displayName = state.firstName && state.lastName 
    ? `${state.firstName} ${state.lastName}`
    : user?.username || 'User';

  return (
    <Box class={hideNav ? 'hidden-nav' : (isCollapsed ? 'main-box' : 'main-box-sidebar')} sx={{ display: "flex" }}>
      <CssBaseline />

      {/* App Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(90deg, #191C35 0%, #002966 100%)',
          boxShadow: 'none'
        }}
      >
        <Toolbar>
          {/* Menu Button */}
          {!hideNav && (
            <IconButton color="inherit" edge="start" onClick={() => setIsCollapsed(!isCollapsed)} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
          )}

          {/* SVG Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Logo style={{ height: "26px", width: "auto" }} />
            <div class="evidence-tools-div">
            {productName && ` | ${productName}`}
            </div>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* User Menu */}
          <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={handleMenu}>
            <Avatar sx={{ bgcolor: "#006BBB", width: 32, height: 32, mr: 1 }}>
              {state.firstName?.[0] || user?.username?.[0] || 'U'}
            </Avatar>
            <Typography variant="body1">{displayName}</Typography>
          </Box>

          {/* Dropdown Menu */}
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {displayName}
              </Typography>
            </MenuItem>
            {state.userCohorts.length > 1 && state.userCohorts.map((cohort) => (
              cohort.cohortId !== state.currentCohort.cohortId && (
                <MenuItem 
                  key={cohort.cohortId}
                  onClick={() => handleCohortSwitch(cohort.cohortId)}
                >
                  Switch to {cohort.cohortName}
                </MenuItem>
              )
            ))}
            <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      {!hideNav && (
        <Drawer
          variant="permanent"
          sx={{
            display: hideNav ? 'none' : 'block',
            width: isCollapsed ? collapsedWidth : drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: isCollapsed ? collapsedWidth : drawerWidth,
              boxSizing: "border-box",
              transition: "width 0.3s",
              marginTop: "64px",
            },
          }}
        >
          <List sx={{ p: 0 }}>
            {menuItems.map((item) => (
              <ListItemButton
                key={item.text}
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
                sx={{
                  display: "flex",
                  justifyContent: isCollapsed ? "center" : "flex-start",
                  padding: "10px",
                  minHeight: "48px",
                  "&.Mui-selected": {
                    backgroundColor: "rgba(0, 107, 187, 0.2)",
                    borderLeft: "4px solid #006BBB",
                  },
                }}
              >
                {item.icon}
                {!isCollapsed && (
                  <ListItemText 
                    primary={item.text} 
                    sx={{ 
                      ml: 2,
                      minWidth: 0,
                      '& .MuiListItemText-primary': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }
                    }} 
                  />
                )}
              </ListItemButton>
            ))}
          </List>
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        className="main-page-div"
        component="main"
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: "calc(100vh - 64px)",
          marginTop: "64px",
          marginLeft: hideNav ? 0 : (isCollapsed ? collapsedWidth : drawerWidth),
          transition: "margin-left 0.3s",
          overflow: "auto"
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
